// Functional conversion of Table and Row components

import React, { useRef, useCallback, useState } from 'react'
import './Table.scss'
import ActionButton, { ActionButtonGroup } from '../ActionButton/ActionButton'
import { BASE_URL } from '../../requests'
import DownloadIcon from '@tecuity/sos-icons/build/react-icons/download-box'
import Pager from '../Pager/Pager'
//import { announce } from '../Announcer/announce'

const Table = ({
  actionCell = false,
  actionCellClassName = '',
  animated = true,
  className = '',
  firstCellInteractive: interactive,
  hidden,
  innerRef = () => {},
  onHeaderLabelClicked,
  rowCount = false,
  rows = [],
  selectedRowIndex,
  sortable,
  sortBy,
  sortByDirection = '',
  template = [],
  totalRows = null,
  category = '',
  onPageSizeChange = () => {},
  onPrevClicked = () => {},
  onNextClicked = () => {},
  isServerPaginated,
  actionMap = {},
  onCellClick,
  announce: announceFn,
}) => {
  const rowRefs = useRef([])
  // const prevRows = useRef(rows)

  const handleCellClick = useCallback(
    (rowIndex, rowId) => {
      if (onCellClick) onCellClick(rowIndex, rowId)
    },
    [onCellClick]
  )

  const getActionsFromMap = useCallback(
    (type = 'default', row) => {
      return typeof actionMap[type] === 'function' ? actionMap[type](row) : actionMap[type]
    },
    [actionMap]
  )

  const checkForClick = (e) => {
    if (sortable && (e.keyCode === 13 || e.keyCode === 32)) {
      e.target.click()
    }
  }

  const firstCellInteractive = interactive === false ? false : true

  return (
    <div style={{ visibility: hidden ? 'hidden' : 'visible' }} className="table-wrapper">
      {isServerPaginated ? (
        <Pager
          onPageSizeChange={onPageSizeChange}
          onPrevClicked={onPrevClicked}
          onNextClicked={onNextClicked}
          category={category}
          hidden={hidden}
        />
      ) : (
        <div
          tabIndex={-1}
          id="row-count"
          className="row-count"
          role="status"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div className={'row-count-text ' + category}>
            {rowCount && rows.length > 0 ? `Results: ${totalRows || rows.length}` : ''}
          </div>
        </div>
      )}

      <table ref={innerRef} className={'div-table ' + className} role="table">
        {template && rows.length > 0 && (
          <thead
            className={'div-table-heading ' + (animated ? 'animated fadeIn fast-animation' : '')}
          >
            <tr role="row">
              {template.map((t, i) => {
                const columnSortable = t.sortable === undefined || t.sortable === 'True'
                return (
                  <th
                    className={`div-table-head ${sortable && columnSortable ? 'sortable' : ''}`}
                    key={i}
                    onClick={() =>
                      sortable && columnSortable && onHeaderLabelClicked
                        ? onHeaderLabelClicked(t.id)
                        : undefined
                    }
                    tabIndex={sortable && columnSortable ? 0 : undefined}
                    onKeyDown={checkForClick}
                    aria-sort={
                      sortable && columnSortable && sortBy === t.id
                        ? sortByDirection.toUpperCase() === 'DESC'
                          ? 'descending'
                          : 'ascending'
                        : undefined
                    }
                    role="columnheader"
                    aria-label={
                      sortable && columnSortable && sortBy === t.id
                        ? `sorted by ${t.label} ${
                            sortByDirection.toUpperCase() === 'DESC' ? 'descending' : 'ascending'
                          }`
                        : ''
                    }
                  >
                    <div className="flex-row">
                      <span className="header-label">{t.label}</span>
                      {sortable && columnSortable && (
                        <SortIndicator
                          sortByDirection={sortBy === t.id ? sortByDirection.toUpperCase() : ''}
                        />
                      )}
                    </div>
                  </th>
                )
              })}
              {actionCell && <th className="div-table-head">Actions</th>}
            </tr>
          </thead>
        )}

        <tbody className="div-table-body">
          {rows.map((row, i) => {
            const actions = getActionsFromMap(row.TYPE, row)
            return (
              <Row
                key={row.ID + ',' + i}
                cells={row}
                template={template}
                selected={i === selectedRowIndex}
                onCellClick={handleCellClick}
                alert={row.ALERT_YN}
                rowIndex={i + 1}
                actionCellClassName={actionCellClassName}
                firstCellInteractive={firstCellInteractive && !row.NoDrawerAllowed}
                ref={(ref) => {
                  if (ref) rowRefs.current[i] = ref
                }}
                actionCell={actionCell ? actions || getActionsFromMap('default') : null}
              />
            )
          })}
        </tbody>
      </table>

      {isServerPaginated && (
        <Pager
          onPageSizeChange={onPageSizeChange}
          onPrevClicked={onPrevClicked}
          onNextClicked={onNextClicked}
          category={category}
          hidden={hidden}
        />
      )}
    </div>
  )
}

const Row = React.forwardRef(
  (
    {
      label,
      template,
      cells,
      onCellClick,
      selected,
      actionCell = [],
      actionCellClassName,
      firstCellInteractive,
    },
    ref
  ) => {
    const [highlighted, setHighlighted] = useState(false)

    React.useImperativeHandle(ref, () => ({
      highlight: () => setHighlighted(true),
    }))

    const checkForClick = (e) => {
      const code = e.charCode || e.keyCode
      if (code === 32 || code === 13) {
        const isActionButton = Array.from(e.target.parentElement.parentElement.classList).includes(
          'action-button'
        )
        if (!isActionButton) {
          e.preventDefault()
          onCellClick(cells)
        }
      }
    }

    return (
      <tr
        role='row'
        className={`div-table-row ${selected ? 'selected' : ''} ${highlighted ? 'highlighted' : ''}`}
        aria-label={label}
        onKeyPress={checkForClick}
      >
        {template.map((t, i) => {
          const isFirstInteractiveCell = firstCellInteractive && i === 0

          const cellContent = (
            <React.Fragment>
              {typeof t.render === 'function' ? (
                <span className="cell">{t.render(cells[t.id])}</span>
              ) : Array.isArray(cells[t.id]) ? (
                cells[t.id].map((cellData, j) => (
                  <span className="cell" key={j}>
                    {cellData}
                  </span>
                ))
              ) : (
                <span className="cell">
                  {t.id === 'DOWNLOAD_LINK' ? (
                    <a className="download-link" href={`${BASE_URL}${cells[t.id]}`}>
                      <DownloadIcon />
                      Download
                    </a>
                  ) : cells[t.id] && typeof cells[t.id] === 'object' ? (
                    <span className={`${cells[t.id].RedFlagStatus ? 'red-flag-status-cell' : ''}`}>
                      {cells[t.id].Description}
                    </span>
                  ) : (
                    cells[t.id]
                  )}
                </span>
              )}
              {isFirstInteractiveCell && <span className="icon icon-caret-right" />}
            </React.Fragment>
          )

          return (
            <td
              role='gridcell'
              key={i}
              className={`div-table-cell ${selected ? 'selected' : ''} ${
                firstCellInteractive ? 'interactive' : ''
              }`}
              onClick={() => isFirstInteractiveCell && onCellClick(cells)}
            >
              {cells.ALERT && i === 0 && (
                <div className="badge row-alert" key={i}>
                  !
                </div>
              )}

              {isFirstInteractiveCell ? (
                <div role="button" tabIndex={0} className="interactive-cell-button">
                  {cellContent}
                </div>
              ) : (
                cellContent
              )}
            </td>
          )
        })}

        {actionCell && (
          <td className={`div-table-cell no-padding ${selected ? 'selected' : ''} action-cell`} role='gridcell'>
            <ActionButtonGroup
              buttonSize="extra-small"
              className={`${actionCellClassName} table-button`}
            >
              {cells.NO_ACTIONS !== true &&
                actionCell.map((button, i) => {
                  const { getIsVisible, label, onClick, disabled } = button
                  const isVisible = getIsVisible ? getIsVisible(cells) : true
                  const buttonLabel = typeof label === 'function' ? label(cells) : label

                  return isVisible ? (
                    <ActionButton
                      hideLabel
                      tooltip
                      {...button}
                      label={buttonLabel}
                      onClick={() => onClick(cells)}
                      key={i}
                      flex
                      disabled={disabled || false}
                    />
                  ) : null
                })}
            </ActionButtonGroup>
          </td>
        )}
      </tr>
    )
  }
)

const SortIndicator = ({ sortByDirection }) => (
  <div className="sort-indicator-wrapper">
    <div
      aria-sort={sortByDirection ? (sortByDirection === 'DESC' ? 'descending' : 'ascending') : ''}
      className={`sort-indicator ${
        sortByDirection ? (sortByDirection === 'DESC' ? 'descending' : 'ascending') : ''
      }`}
    />
  </div>
)

export default Table;